import { useEffect, useRef, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import { useTranslate } from 'shared/lib/i18n'
import { cn } from 'shared/lib/utils'
import { BagLabelPrint } from './BagLabel'
import { ToneBanner, TONE_CLASS } from 'shared/components/tone-banner'

/**
 * Closing a Bag, in a dialog over the sorting — the ribbon of scans (spec §12, §26 view A; ADR-0025; ticket 26).
 * One press: no seal, no weight (ADR-0047) — the cursor stands on «Закрыть мешок»,
 * so Enter closes. Once closed, its label (`BagLabel`: the code as a barcode, the
 * Point, the date) goes to the printer at once, and «Печать этикетки» prints it again.
 *
 * The calls are injected (`loadBag`, `closeBag`, `print`) so the behaviour is
 * tested without a backend; they default to the real tRPC client and the browser.
 */

export type HubBagView = RouterOutputs['sorting']['Bag']
export type HubBagCloseRequest = RouterInputs['sorting']['Close']
export type HubBagCloseAnswer = RouterOutputs['sorting']['Close']
export type HubBagRow = RouterOutputs['sorting']['Bags'][number]

const loadBagViaTrpc = (code: string) => trpcClient.sorting.Bag.query({ code })
const closeBagViaTrpc = (input: HubBagCloseRequest) =>
	trpcClient.sorting.Close.mutate(input)

const failure = (error: unknown) =>
	error instanceof Error ? error.message : String(error)

export interface HubBagCloseProps {
	bag: HubBagRow
	/** Called when the bag is closed (so the list re-reads). */
	onSealed: () => void
	/** Leave the ribbon: cancel, or «Готово» after the close. */
	onDone: () => void
	/** Test seam: the bag with its parcels. */
	loadBag?: (code: string) => Promise<HubBagView>
	/** Test seam: the close. */
	closeBag?: (input: HubBagCloseRequest) => Promise<HubBagCloseAnswer>
	/** Test seam: the browser's print of the label. */
	print?: () => void
}

export function HubBagClose({
	bag,
	onSealed,
	onDone,
	loadBag = loadBagViaTrpc,
	closeBag = closeBagViaTrpc,
	print,
}: HubBagCloseProps) {
	const { t } = useTranslate()
	const [view, setView] = useState<HubBagView | null>(null)
	const [sending, setSending] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [answer, setAnswer] = useState<HubBagCloseAnswer | null>(null)
	const confirmRef = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		loadBag(bag.code).then(setView, () => {
			// The contents are a convenience; closing does not need them.
		})
	}, [loadBag, bag.code])

	const confirm = async () => {
		if (sending) return
		setSending(true)
		setError(null)
		try {
			const res = await closeBag({ code: bag.code })
			setAnswer(res)
			if (res.outcome === 'sealed') onSealed()
		} catch (e) {
			setError(failure(e))
		} finally {
			setSending(false)
		}
	}

	const sealed = answer?.outcome === 'sealed' ? answer.bag : null

	return (
		<Dialog open onOpenChange={(open) => !open && !sending && onDone()}>
			<DialogContent
				className='sm:max-w-lg'
				data-testid='hub-bag-close'
				{...(sealed ? { 'aria-describedby': undefined } : {})}
				// The cursor stands on «Закрыть мешок», so Enter closes the Bag.
				onOpenAutoFocus={(event) => {
					event.preventDefault()
					confirmRef.current?.focus()
				}}
			>
				<DialogHeader>
					<DialogTitle className='text-lg'>
						{t('hubSorting.closeTitle', { code: bag.code, point: bag.pointCode })}
					</DialogTitle>
					{!sealed && <DialogDescription>{t('hubSorting.closeHint')}</DialogDescription>}
				</DialogHeader>

				{sealed ? (
					<div className='flex flex-col gap-4'>
						<ToneBanner
							tone='ready'
							title={t('hubSorting.sealedTitle')}
							detail={t('hubSorting.sealedBody', {
								code: sealed.code,
								point: sealed.pointCode,
								count: String(sealed.parcelCount),
							})}
							testId='hub-bag-sealed'
							detailTestId='hub-bag-sealed-body'
						>
							<span className='sr-only' data-testid='hub-bag-sealed-code'>
								{sealed.code}
							</span>
						</ToneBanner>
						<BagLabelPrint bag={sealed} printNow print={print} />
						<DialogFooter>
							<Button type='button' data-testid='hub-bag-done' onClick={onDone}>
								{t('hubSorting.sealedDone')}
							</Button>
						</DialogFooter>
					</div>
				) : (
					<>
						{view && view.parcels.length > 0 && (
							<div className='flex flex-col gap-1.5'>
								<div className='text-sm font-medium'>
									{t('hubSorting.parcelsInBag')}{' '}
									<span className='text-muted-foreground'>{view.parcelCount}</span>
								</div>
								<ul
									className='grid max-h-48 grid-cols-2 gap-x-4 gap-y-0.5 overflow-auto rounded-md border px-3 py-2 text-sm'
									data-testid='hub-bag-parcels'
								>
									{view.parcels.map((p) => (
										<li key={p.parcelId} className='font-mono'>
											{p.trackingCode}
										</li>
									))}
								</ul>
							</div>
						)}

						{answer?.outcome === 'refused' && (
							<div
								role='status'
								className={cn('rounded-lg border px-4 py-3 font-semibold', TONE_CLASS.refused)}
								data-testid='hub-bag-refused'
								data-tone='refused'
								data-reason={answer.reason}
							>
								{t(`hubBagCloseRefusal.${answer.reason}`)}
							</div>
						)}
						{error && (
							<p role='alert' className='text-sm text-destructive' data-testid='hub-bag-error'>
								{t('hubSorting.closeFailed', { message: error })}
							</p>
						)}
						<DialogFooter>
							<Button
								type='button'
								variant='outline'
								data-testid='hub-bag-cancel'
								disabled={sending}
								onClick={onDone}
							>
								{t('hubSorting.closeCancel')}
							</Button>
							<Button
								ref={confirmRef}
								type='button'
								data-testid='hub-bag-confirm'
								disabled={sending}
								onClick={confirm}
							>
								{t('hubSorting.closeConfirm')}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	)
}
